/**
 * UiProvider - Frontend UI Context Provider
 *
 * Populates FrontendThemeContext with frontend-specific variants (BannerSlider, ProductSlider, etc.).
 * The shared UI primitives are styled by the wrappers in components/ui (configs imported
 * directly), not through context.
 */

import { createContext, useContext } from 'react'

import * as BannerSliderConfig from '../../configs/bannerSlider.config'
import * as ButtonConfig from '../../configs/button.config'
import * as CategoryCardsConfig from '../../configs/categoryCards.config'
import * as ProductSliderConfig from '../../configs/productSlider.config'
import * as PromoBlockConfig from '../../configs/promoBlock.config'
import * as StarsConfig from '../../configs/stars.config'

import type {
  AddToCartVariants,
  BannerSliderVariants,
  CategoryCardsVariants,
  ProductSliderVariants,
  PromoBlockVariants,
  StarsVariants,
} from '../../types'

// =============================================================================
// FRONTEND THEME CONTEXT
// =============================================================================

/** Every variant group of types/variants.ts, in one context value. */
export type FrontendThemeContextValue = StarsVariants &
  BannerSliderVariants &
  ProductSliderVariants &
  PromoBlockVariants &
  CategoryCardsVariants &
  AddToCartVariants

export const FrontendThemeContext = createContext<FrontendThemeContextValue | null>(null)

export function useFrontendTheme(): FrontendThemeContextValue {
  const context = useContext(FrontendThemeContext)
  if (!context) {
    throw new Error('useFrontendTheme must be used within Frontend UiProvider')
  }
  return context
}

// =============================================================================
// PROVIDER
// =============================================================================

export interface UiProviderProps {
  children: React.ReactNode
}

const frontendConfig: FrontendThemeContextValue = {
  // Stars
  starsContainerVariants: StarsConfig.starsContainerVariants,
  starIconVariants: StarsConfig.starIconVariants,

  // Banner Slider
  bannerSectionVariants: BannerSliderConfig.bannerSectionVariants,
  bannerItemVariants: BannerSliderConfig.bannerItemVariants,
  bannerLinkVariants: BannerSliderConfig.bannerLinkVariants,
  bannerImageVariants: BannerSliderConfig.bannerImageVariants,
  bannerFallbackVariants: BannerSliderConfig.bannerFallbackVariants,
  bannerFallbackContentVariants: BannerSliderConfig.bannerFallbackContentVariants,
  bannerFallbackTitleVariants: BannerSliderConfig.bannerFallbackTitleVariants,
  bannerFallbackCtaVariants: BannerSliderConfig.bannerFallbackCtaVariants,
  bannerNavButtonVariants: BannerSliderConfig.bannerNavButtonVariants,
  bannerNavIconVariants: BannerSliderConfig.bannerNavIconVariants,
  bannerSwiperVariants: BannerSliderConfig.bannerSwiperVariants,
  bannerSkeletonVariants: BannerSliderConfig.bannerSkeletonVariants,
  swiperPaginationBullet: BannerSliderConfig.SWIPER_PAGINATION_BULLET,
  swiperPaginationBulletActive: BannerSliderConfig.SWIPER_PAGINATION_BULLET_ACTIVE,

  // Product Slider
  productSliderSectionVariants: ProductSliderConfig.productSliderSectionVariants,
  productSliderWrapperVariants: ProductSliderConfig.productSliderWrapperVariants,
  productSliderSwiperVariants: ProductSliderConfig.productSliderSwiperVariants,
  productSliderSlideVariants: ProductSliderConfig.productSliderSlideVariants,
  productSliderSkeletonGridVariants: ProductSliderConfig.productSliderSkeletonGridVariants,
  productSliderGridVariants: ProductSliderConfig.productSliderGridVariants,
  productSliderGridItemVariants: ProductSliderConfig.productSliderGridItemVariants,

  // Category Cards
  categoryCardsGridVariants: CategoryCardsConfig.categoryCardsGridVariants,
  categoryCardItemVariants: CategoryCardsConfig.categoryCardItemVariants,
  categoryCardImageWrapperVariants: CategoryCardsConfig.categoryCardImageWrapperVariants,
  categoryCardImageVariants: CategoryCardsConfig.categoryCardImageVariants,
  categoryCardTitleVariants: CategoryCardsConfig.categoryCardTitleVariants,
  categoryCardSkeletonVariants: CategoryCardsConfig.categoryCardSkeletonVariants,

  // Button (Frontend-only)
  addToCartLabelVariants: ButtonConfig.addToCartLabelVariants,

  // Promo Block
  promoBlockSectionVariants: PromoBlockConfig.promoBlockSectionVariants,
  promoBlockContainerVariants: PromoBlockConfig.promoBlockContainerVariants,
  promoBlockImageSectionVariants: PromoBlockConfig.promoBlockImageSectionVariants,
  promoBlockImageLinkVariants: PromoBlockConfig.promoBlockImageLinkVariants,
  promoBlockImageVariants: PromoBlockConfig.promoBlockImageVariants,
  promoBlockContentSectionVariants: PromoBlockConfig.promoBlockContentSectionVariants,
  promoBlockContentInnerVariants: PromoBlockConfig.promoBlockContentInnerVariants,
  promoBlockTitleVariants: PromoBlockConfig.promoBlockTitleVariants,
  promoBlockDescriptionVariants: PromoBlockConfig.promoBlockDescriptionVariants,
  promoBlockSkeletonContentVariants: PromoBlockConfig.promoBlockSkeletonContentVariants,
  promoBlockSkeletonInnerVariants: PromoBlockConfig.promoBlockSkeletonInnerVariants,
}

/**
 * UiProvider - Provides frontend-specific variants via FrontendThemeContext
 */
export function UiProvider({ children }: UiProviderProps) {
  return <FrontendThemeContext value={frontendConfig}>{children}</FrontendThemeContext>
}
